// Centralized status/color configurations to avoid duplication across components.
// Each entity domain exports its own typed config.

// ─── CRM Account Status ─────────────────────────────────────────────
export const ACCOUNT_STATUS_CONFIG = {
  customer: { label: 'Client', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  prospect: { label: 'Prospect', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  churned: { label: 'Perdu', color: 'bg-red-100 text-red-700', dotColor: 'bg-red-500' },
} as const;

// ─── CRM Contact Status ─────────────────────────────────────────────
export const CONTACT_STATUS_CONFIG = {
  active: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  inactive: { label: 'Inactif', color: 'bg-neutral-100 text-neutral-600', dotColor: 'bg-neutral-400' },
} as const;

// ─── Lead Status ─────────────────────────────────────────────────────
export const LEAD_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  new: { label: 'Nouveau', color: 'bg-blue-50 text-[#0066FF]' },
  info_collected: { label: 'Infos collectées', color: 'bg-sky-100 text-sky-700' },
  slot_selected: { label: 'RDV planifié', color: 'bg-violet-100 text-violet-700' },
  subscription_created: { label: 'Abonné', color: 'bg-emerald-100 text-emerald-700' },
  onboarding: { label: 'Onboarding', color: 'bg-purple-100 text-purple-700' },
  active: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700' },
  lost: { label: 'Perdu', color: 'bg-red-100 text-red-700' },
};

// ─── Lead Type ───────────────────────────────────────────────────────
export const LEAD_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  standard: { label: 'Standard', color: 'bg-blue-50 text-[#0066FF]' },
  pilote: { label: 'Pilote', color: 'bg-amber-100 text-amber-700' },
  grand_compte: { label: 'Grand Compte', color: 'bg-purple-100 text-purple-700' },
};

// ─── Opportunity Status ──────────────────────────────────────────────
export const OPPORTUNITY_STATUS_CONFIG = {
  open: { label: 'En cours', color: 'bg-blue-50 text-[#0066FF]' },
  won: { label: 'Gagnée', color: 'bg-emerald-100 text-emerald-700' },
  lost: { label: 'Perdue', color: 'bg-red-100 text-red-700' },
} as const;

// ─── Pipeline Stage Colors (shared by OpportunityTable, OpportunityKanban, LeadTable) ──
export const STAGE_COLOR_MAP: Record<string, string> = {
  gray: 'bg-neutral-100 text-neutral-700',
  blue: 'bg-blue-50 text-[#0066FF]',
  purple: 'bg-purple-100 text-purple-700',
  indigo: 'bg-violet-100 text-violet-700',
  orange: 'bg-orange-100 text-orange-700',
  amber: 'bg-amber-100 text-amber-700',
  green: 'bg-emerald-100 text-emerald-700',
  red: 'bg-red-100 text-red-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  teal: 'bg-teal-100 text-teal-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  sky: 'bg-sky-100 text-sky-700',
  violet: 'bg-violet-100 text-violet-700',
};

// Kanban variant with bg/border/badge for column headers
export const STAGE_COLOR_KANBAN: Record<string, { bg: string; border: string; badge: string }> = {
  gray: { bg: 'bg-neutral-50', border: 'border-neutral-200', badge: 'bg-neutral-200 text-neutral-700' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-200 text-[#0066FF]' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-200 text-purple-700' },
  indigo: { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-200 text-violet-700' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-200 text-orange-700' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-200 text-amber-700' },
  green: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-200 text-emerald-700' },
  red: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-200 text-red-700' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-200 text-emerald-700' },
  teal: { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-200 text-teal-700' },
  cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', badge: 'bg-cyan-200 text-cyan-700' },
  sky: { bg: 'bg-sky-50', border: 'border-sky-200', badge: 'bg-sky-200 text-sky-700' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-200 text-violet-700' },
};

// ─── Meeting Report Status ───────────────────────────────────────────
export type MeetingReportStatusKey = 'draft' | 'transcript_fetched' | 'cr_generated' | 'actions_validated' | 'email_sent' | 'error' | 'no_meeting';

export const MEETING_REPORT_STATUS_CONFIG: Record<MeetingReportStatusKey, { label: string; color: string }> = {
  draft: { label: 'Brouillon', color: 'bg-neutral-100 text-neutral-700' },
  transcript_fetched: { label: 'Transcript récupéré', color: 'bg-blue-50 text-[#0066FF]' },
  cr_generated: { label: 'CR généré', color: 'bg-purple-100 text-purple-700' },
  actions_validated: { label: 'Actions validées', color: 'bg-emerald-100 text-emerald-700' },
  email_sent: { label: 'Email envoyé', color: 'bg-emerald-100 text-emerald-700' },
  error: { label: 'Erreur', color: 'bg-red-100 text-red-700' },
  no_meeting: { label: 'Réunion non vérifiée', color: 'bg-amber-100 text-amber-700' },
};
